import { h, clear } from "@/lib/dom";
import { icon, type IconName } from "@/lib/icons";
import {
  button,
  emptyState,
  confirmDialog,
  openModal,
  toast,
  badge,
  spinner,
} from "@/components/ui";
import {
  topicsService,
  materialsService,
  notesService,
  subjectsService,
  homeworkService,
  pbErrorMessage,
} from "@/services";
import { homeworkBadge } from "@/features/homework/list";
import { readBadMaterials } from "@/services/document/badMaterials";
import { openTopicSettings } from "@/features/topics/topicSettings";
import { examTypeLabel } from "@/features/topics/examFields";
import { formatBytes, formatDate, formatCountdown, daysUntil, wordCount } from "@/lib/format";
import { navigate, type RouteContext } from "@/app/router";
import { offlineSync } from "@/services/offline/offline-sync.service";
import { openCropDialog } from "@/features/materials/cropDialog";
import { fileRef } from "@/markdown/fileref";
import { localFiles } from "@/services/document/localFiles";
import { pullTopic, schedulePush } from "@/services/document/fileSync";
import type { Topic, Material, NoteKind, Homework, NoteDocument, Subject } from "@/types/models";

/**
 * Thema-Workspace. Bereiche untereinander:
 *   1. Materialien   2. Mitschrift | Lernzettel (zwei Spalten)   3. Üben
 * Einstellungen = Zahnrad oben rechts (Modal).
 */
interface TopicData {
  topic: Topic;
  materials: Material[];
  notes: NoteDocument[];
  subject: Subject | undefined;
  homework: Homework[];
  /** Dateien, deren lokaler Stand noch nicht hochgeladen ist (localFiles.ts). */
  unsynced: Set<string>;
}

function unsyncedOf(topicId: string): Promise<Set<string>> {
  return localFiles
    .pending()
    .then((files) => new Set(files.filter((f) => f.topic === topicId).map((f) => f.id)))
    .catch(() => new Set<string>());
}

async function loadFromNetwork(id: string): Promise<TopicData | null> {
  const materialsPromise = materialsService
    .listByTopic(id)
    .then(async (list) => {
      await offlineSync.saveMaterials(id, list).catch(() => undefined);
      // Dateien dieses Themas schon mal aufs Gerät holen – wer das Thema
      // ansieht, öffnet als Nächstes oft das Arbeits-PDF.
      void pullTopic(id, list).catch(() => undefined);
      return (await offlineSync.materialsLocal(id).catch(() => null)) ?? list;
    })
    .catch(() => offlineSync.materialsLocal(id).then((m) => m ?? []));
  const notesPromise = notesService.listByTopic(id).catch(() => offlineSync.loadNotesList(id));

  let topic: Topic;
  try {
    topic = await topicsService.get(id);
  } catch {
    const cached = (await offlineSync.loadTopics().catch(() => null))?.find((t) => t.id === id);
    if (!cached) return null;
    topic = cached;
  }

  const [materials, notes, subject, homework, unsynced] = await Promise.all([
    materialsPromise,
    notesPromise,
    subjectsService
      .get(topic.subject)
      .catch(async () => (await offlineSync.loadSubjects().catch(() => null))?.find((s) => s.id === topic.subject)),
    homeworkService.listByTopicWithFallback(id, topic.subject).catch(() => [] as Homework[]),
    unsyncedOf(id),
  ]);
  return { topic, materials, notes, subject, homework, unsynced };
}

async function loadFromCache(id: string): Promise<TopicData | null> {
  const [topics, materials, subjects, homework, unsynced] = await Promise.all([
    offlineSync.loadTopics(),
    offlineSync.materialsLocal(id),
    offlineSync.loadSubjects(),
    offlineSync.loadHomeworkList(),
    unsyncedOf(id),
  ]);
  const topic = topics?.find((t) => t.id === id);
  if (!topic || !materials) return null;
  return {
    topic,
    materials,
    unsynced,
    notes: await offlineSync.loadNotesList(id),
    subject: subjects?.find((s) => s.id === topic.subject),
    homework: (homework ?? [])
      .filter((hw) => hw.topic === id || (hw.subject === topic.subject && !hw.topic))
      .sort((a, b) => (a.dueDate ?? "").localeCompare(b.dueDate ?? "")),
  };
}

let skipCacheOnce = false;

export async function TopicPage(ctx: RouteContext): Promise<HTMLElement> {
  const id = ctx.params.id;
  const useCache = !skipCacheOnce && navigator.onLine;
  skipCacheOnce = false;
  const network = loadFromNetwork(id);
  const cached = useCache ? await loadFromCache(id).catch(() => null) : null;
  if (!cached) {
    const data = await network;
    return data ? buildTopicView(id, data) : notFoundView();
  }
  let view = buildTopicView(id, cached);
  void network.then((fresh) => {
    if (!fresh || !view.isConnected || view.querySelector(".is-pending")) return;
    const next = buildTopicView(id, fresh);
    if (next.innerHTML === view.innerHTML) return;
    view.replaceWith(next);
    view = next;
  }, () => undefined);
  return view;
}

function notFoundView(): HTMLElement {
  return h(
    "div",
    null,
    backButton(),
    h(
      "div",
      { class: "card card-pad mt-2" },
      emptyState({
        icon: "layers",
        title: "Thema nicht gefunden",
        text: "Dieses Thema existiert nicht (mehr) – oder du bist offline und es wurde noch nicht zwischengespeichert.",
      })
    )
  );
}

function buildTopicView(id: string, { topic, materials, notes, subject, homework, unsynced }: TopicData): HTMLElement {
  const refresh = () => {
    skipCacheOnce = true;
    navigate(`/topic/${id}`);
  };
  /** Nach rein lokalen Änderungen: aus der Geräteablage neu zeichnen, nicht aufs Netz warten. */
  const refreshLocal = () => navigate(`/topic/${id}`);

  const noteOf = (kind: NoteKind) => notes.find((n) => n.kind === kind);
  const examDays = daysUntil(topic.examDate ?? "");
  const examBadgeKind =
    examDays === null ? "" : examDays < 0 ? "archived" : examDays <= 7 ? "upcoming" : "active";
  const examMeta = topic.examDate
    ? [
        formatDate(topic.examDate),
        examTypeLabel(topic.examType),
      ].filter(Boolean)
    : [];

  // ---- Header ----
  const header = h(
    "div",
    { class: "topic-top" },
    button({ icon: "chevron-left", variant: "ghost", title: "Zurück", onClick: () => navigate("/") }),
    h(
      "div",
      { class: "t-headings" },
      h("h1", { title: topic.title }, topic.title),
      topic.description ? h("div", { class: "topic-crumb" }, topic.description) : null,
      topic.examDate
        ? h(
            "div",
            { class: "topic-exam-meta" },
            badge(formatCountdown(topic.examDate), examBadgeKind),
            h("span", null, examMeta.join(" · "))
          )
        : null
    ),
    homeworkBadge({
      topicId: id,
      subjectId: topic.subject,
      subjectTitle: subject?.title,
      accent: subject?.color,
      items: homework,
    }),
    button({
      icon: "settings",
      variant: "ghost",
      title: "Einstellungen",
      onClick: () =>
        void openTopicSettings({
          topic,
          onChanged: refresh,
        }),
    })
  );

  // ---- Materialien ----

  // Dateien, die sich beim letzten Öffnen des Arbeitsdokuments nicht lesen
  // ließen (siehe badMaterials.ts) – erst dann wird rot markiert, nicht schon
  // wegen einer fehlenden Endung.
  const badMaterials = readBadMaterials(id);


  /** Eine Datei mit ihrem Pfad – bei Ordnern zählt der Pfad für die Reihenfolge. */
  interface Upload {
    file: File;
    path: string;
  }

  /**
   * Was in der Ablagefläche landet: einzelne Dateien oder ganze Ordner. Von
   * einem Ordner werden alle Dateien darin übernommen, die Struktur selbst
   * nicht – nur die Reihenfolge, dafür sorgt die Sortierung nach Pfad mit
   * natürlicher Zahlenfolge („Seite 2" vor „Seite 10").
   */
  async function fromDataTransfer(dt: DataTransfer): Promise<Upload[]> {
    const entries = Array.from(dt.items ?? [])
      .map((item) => item.webkitGetAsEntry?.())
      .filter((entry): entry is FileSystemEntry => Boolean(entry));
    if (!entries.length) {
      return Array.from(dt.files).map((file) => ({ file, path: file.name }));
    }

    const out: Upload[] = [];
    const walk = async (entry: FileSystemEntry, prefix: string): Promise<void> => {
      if (entry.isFile) {
        const file = await new Promise<File | null>((resolve) =>
          (entry as FileSystemFileEntry).file(resolve, () => resolve(null))
        );
        if (file) out.push({ file, path: prefix + file.name });
        return;
      }
      if (!entry.isDirectory) return;
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      // `readEntries` liefert je Aufruf nur einen Teil – bis zur leeren
      // Antwort weiterfragen, sonst fehlen bei großen Ordnern Dateien.
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((resolve) =>
          reader.readEntries(resolve, () => resolve([]))
        );
        if (!batch.length) break;
        for (const child of batch) await walk(child, `${prefix}${entry.name}/`);
      }
    };
    for (const entry of entries) await walk(entry, "");
    return out;
  }

  /** Platzhalter-Zeile, solange die Datei noch hochlädt. */
  function pendingRow(name: string): HTMLElement {
    return h(
      "div",
      { class: "material-row is-pending" },
      h("span", { class: "m-icon" }, spinner()),
      h(
        "div",
        { class: "m-body" },
        h("div", { class: "m-name", title: name }, name),
        h("div", { class: "m-meta" }, "Lädt hoch …"),
        h("div", { class: "upload-progress-track" }, h("div", { class: "upload-progress-bar" }))
      )
    );
  }

  const pendingList = h("div", { class: "material-list pending-list" });

  /**
   * Nacheinander hochladen, von oben nach unten: jede Zeile steht sofort da
   * und tauscht ihren Kreis gegen das Dateisymbol, sobald sie durch ist. Bei
   * einem Schwung Scans sieht man so, wie weit es ist.
   */
  async function uploadFiles(uploads: Upload[]) {
    const collator = new Intl.Collator("de", { numeric: true, sensitivity: "base" });
    const list = [...uploads].sort((a, b) => collator.compare(a.path, b.path));
    if (!list.length) return;

    clear(pendingList);
    const rows = list.map((upload) => pendingRow(upload.file.name));
    for (const row of rows) pendingList.appendChild(row);

    let ok = 0;
    for (const [i, upload] of list.entries()) {
      try {
        let file = upload.file;
        if (file.name.toLowerCase().endsWith(".goodnotes")) {
          const { convertGoodnotesToPdf } = await import("@/services/document/goodnotesConvert");
          const converted = await convertGoodnotesToPdf(await file.arrayBuffer());
          if (!converted) {
            throw new Error(
              "Reines Handschrift-Notizbuch – daraus lässt sich keine Handschrift auslesen. Bitte in GoodNotes selbst „Exportieren → PDF“ nutzen."
            );
          }
          if (converted.hasUnrecoverableInk) {
            toast(`„${file.name}": Nur eingebettete PDFs/Bilder übernommen – handschriftliche Anmerkungen gehen dabei verloren.`, "error");
          }
          file = new File([converted.blob], file.name.replace(/\.goodnotes$/i, ".pdf"), { type: "application/pdf" });
        }
        const bar = rows[i].querySelector<HTMLElement>(".upload-progress-bar")!;
        if (/^(audio|video)\//.test(file.type)) {
          // Ton/Video gehören nicht ins Arbeits-PDF und werden direkt vom
          // Server abgespielt – die gehen wie bisher gleich hoch.
          await materialsService.upload(id, file, undefined, (fraction) => {
            bar.style.width = `${Math.round(fraction * 100)}%`;
          });
        } else {
          // PDFs/Bilder: sofort auf dem Gerät und damit sofort nutzbar, der
          // Upload läuft im Hintergrund (fileSync.ts).
          await localFiles.addLocal(id, file, file.name, file.name);
          bar.style.width = "100%";
        }
        ok++;
        rows[i].classList.remove("is-pending");
        rows[i].querySelector(".m-icon")!.innerHTML = icon("file", 22);
        rows[i].querySelector(".m-meta")!.textContent = "Fertig";
      } catch (err) {
        rows[i].classList.add("is-error");
        rows[i].querySelector(".m-meta")!.textContent = pbErrorMessage(err);
      }
    }
    if (ok) {
      toast(ok === 1 ? "Gespeichert." : `${ok} Dateien gespeichert.`, "success");
      schedulePush(500);
    }
    // Bei (Teil-)Fehlschlag bleibt die gerade gesetzte Fehlerzeile stehen –
    // ein sofortiger Refresh (voller Re-Render der Seite) würde sie wieder wegreißen.
    if (ok === list.length) refreshLocal();
  }

  const fileInput = h("input", {
    type: "file",
    multiple: true,
    style: "display:none",
    onchange: (e: Event) => {
      const input = e.target as HTMLInputElement;
      const picked = Array.from(input.files ?? []).map((file) => ({
        file,
        path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
      }));
      if (picked.length) void uploadFiles(picked);
      // Zurücksetzen: sonst löst dieselbe Auswahl beim zweiten Mal nichts aus.
      input.value = "";
    },
  });

  const dropzone = h(
    "div",
    {
      class: "upload-drop",
      onclick: () => fileInput.click(),
      ondragover: (e: DragEvent) => {
        e.preventDefault();
        dropzone.classList.add("dragover");
      },
      ondragleave: () => dropzone.classList.remove("dragover"),
      ondrop: (e: DragEvent) => {
        e.preventDefault();
        dropzone.classList.remove("dragover");
        if (e.dataTransfer) void fromDataTransfer(e.dataTransfer).then(uploadFiles);
      },
    },
    h("span", { class: "row", html: icon("upload", 26) }),
    h(
      "div",
      { class: "upload-drop-text" },
      "Dateien oder ganze Ordner hierher ziehen – oder klicken"
    ),
    h("div", { class: "upload-drop-hint" }, "PDF, Bilder, Audio – max. 50 MB pro Datei")
  );

  // Zuklappbar; Zustand bleibt pro Thema erhalten. Standard ist ZU – Materialien
  // sind Begleitmaterial, nicht der erste Blick auf ein Thema; wer sie öfter
  // braucht, klappt einmal auf und der Zustand bleibt gemerkt.
  const collapseKey = `mats_collapsed_${id}`;
  const startCollapsed = localStorage.getItem(collapseKey) !== "0";
  const openDocument = () => navigate(`/topic/${id}/document`);

  const materialsList = h("div", { class: "material-list mt-2" }, ...materials.map(materialRow));
  const materialsBody = h(
    "div",
    { class: "section-body" },
    fileInput,
    dropzone,
    pendingList,
    materials.length > 0 ? materialsList : null
  );
  const materialsSection = h(
    "div",
    { class: "card section" + (startCollapsed ? " collapsed" : "") },
    h(
      "div",
      { class: "section-title" },
      h("span", {
        class: "section-chevron",
        html: icon("chevron-down", 16),
        onclick: () => {
          const collapsed = materialsSection.classList.toggle("collapsed");
          try { localStorage.setItem(collapseKey, collapsed ? "1" : "0"); } catch { /* ignore */ }
        },
      }),
      h(
        "span",
        { class: "row section-title-open", title: "Materialien als ein Dokument öffnen", onclick: openDocument },
        h("span", { class: "row", html: icon("file", 18) }),
        "Materialien",
        materials.length ? h("span", { class: "muted text-sm" }, ` (${materials.length})`) : null
      ),
      h("span", { class: "spacer" }),
      materials.length
        ? button({ icon: "eye", variant: "ghost", size: "sm", title: "Als ein Dokument öffnen", onClick: openDocument })
        : null
    ),
    materialsBody
  );

  // ---- Reihenfolge per Ziehen (identisches Muster wie „Fächer ordnen") ----
  // Die Reihenfolge hier bestimmt auch die Seitenfolge im zusammengeführten
  // Arbeits-PDF (siehe services/workspace.service.ts).
  let dragRow: HTMLElement | null = null;

  function getAfterElement(y: number): HTMLElement | null {
    const rows = Array.from(materialsList.querySelectorAll<HTMLElement>(".material-row:not(.dragging)"));
    let closestOffset = -Infinity;
    let closestEl: HTMLElement | null = null;
    for (const el of rows) {
      const box = el.getBoundingClientRect();
      const offset = y - box.top - box.height / 2;
      if (offset < 0 && offset > closestOffset) {
        closestOffset = offset;
        closestEl = el;
      }
    }
    return closestEl;
  }

  const onDragMove = (e: PointerEvent) => {
    if (!dragRow) return;
    const after = getAfterElement(e.clientY);
    if (after == null) materialsList.appendChild(dragRow);
    else materialsList.insertBefore(dragRow, after);
  };
  const onDragEnd = () => {
    if (!dragRow) return;
    document.removeEventListener("pointermove", onDragMove);
    document.removeEventListener("pointerup", onDragEnd);
    document.removeEventListener("pointercancel", onDragEnd);
    dragRow.classList.remove("dragging");
    dragRow = null;
    void persistOrder();
  };

  async function persistOrder(): Promise<void> {
    const ids = Array.from(materialsList.querySelectorAll<HTMLElement>(".material-row")).map(
      (row) => row.dataset.id!
    );
    try {
      await materialsService.reorder(ids, materials);
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  function materialRow(m: Material): HTMLElement {
    const grip = h("span", { class: "manage-grip", title: "Ziehen zum Sortieren", html: icon("grip", 18) });
    const broken = badMaterials.has(m.id);
    const localOnly = m.id.startsWith("local_");
    const media = /^(audio|video)\//.test(m.type ?? "");
    const row = h(
      "div",
      { class: "material-row" + (broken ? " is-broken" : ""), dataset: { id: m.id } },
      grip,
      h("span", {
        class: "m-icon",
        title: broken ? "Diese Datei ließ sich nicht lesen" : undefined,
        html: icon(broken ? "alert-triangle" : "file", 22),
      }),
      h(
        "div",
        { class: "m-body" },
        h("div", { class: "m-name", title: m.title }, m.title),
        h(
          "div",
          { class: "m-meta" },
          broken
            ? "Konnte nicht gelesen werden"
            : [
                m.type,
                formatBytes(m.size),
                unsynced.has(m.id) ? (localOnly ? "nur auf diesem Gerät – wird hochgeladen" : "Änderung wird hochgeladen") : "",
              ]
                .filter(Boolean)
                .join(" · ")
        )
      ),
      ...(!media
        ? [
            button({
              icon: "clock",
              variant: "ghost",
              size: "sm",
              title: "Ältere Versionen",
              onClick: () => void openVersions(m),
            }),
          ]
        : []),
      ...(m.type?.startsWith("image/") && !localOnly
        ? [
            button({
              icon: "crop",
              variant: "ghost",
              size: "sm",
              title: "Zuschneiden",
              onClick: () =>
                void openCropDialog({
                  imageUrl: materialsService.urlFor(m),
                  mimeType: m.type,
                  backupKey: m.id,
                  onSave: async (blob) => {
                    // PocketBase vergibt beim Schreiben eines Datei-Feldes
                    // IMMER einen neuen Dateinamen, auch bei diesem Material
                    // (gleiche ID) – jede `pb:<id>/<datei>`-Kurzform in
                    // Mitschrift/Lernzettel dieses Themas würde sonst ins
                    // Leere zeigen ("nicht gefunden" in der Vorschau).
                    const oldRef = fileRef(m.id, m.file);
                    const updated = await materialsService.saveDetails(m.id, { title: m.title, file: blob, fileName: m.file });
                    const newRef = fileRef(updated.id, updated.file);
                    if (newRef !== oldRef) {
                      const notes = await notesService.listByTopic(id).catch(() => []);
                      for (const n of notes) {
                        if (!n.content.includes(oldRef)) continue;
                        await notesService.save(n.id, n.content.split(oldRef).join(newRef));
                      }
                    }
                    refresh();
                  },
                }),
            }),
          ]
        : []),
      button({
        icon: "trash",
        variant: "ghost",
        size: "sm",
        title: "Löschen",
        onClick: () => void deleteMaterial(m),
      })
    );
    grip.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      dragRow = row;
      row.classList.add("dragging");
      // Zeiger festhalten: ohne das geht das `pointerup` verloren, sobald der
      // Zeiger beim Ziehen den Griff verlässt – die Zeile bleibt dann am
      // Zeiger kleben und die Reihenfolge wird nie geschrieben.
      grip.setPointerCapture(e.pointerId);
      document.addEventListener("pointermove", onDragMove);
      document.addEventListener("pointerup", onDragEnd, { once: true });
      document.addEventListener("pointercancel", onDragEnd, { once: true });
    });
    return row;
  }

  async function deleteMaterial(m: Material) {
    const ok = await confirmDialog({
      title: "Datei löschen?",
      message: `„${m.title}" wird entfernt.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      if (m.id.startsWith("local_")) {
        await localFiles.remove(m.id);
        toast("Datei gelöscht.", "success");
        return refreshLocal();
      }
      await materialsService.remove(m.id);
      await localFiles.remove(m.id).catch(() => undefined);
      toast("Datei gelöscht.", "success");
      refresh();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    }
  }

  /**
   * Ältere Stände einer Datei (localFiles.ts): je Bearbeitungssitzung einer,
   * dazu Stände anderer Geräte, die beim Abgleich ersetzt wurden.
   */
  async function openVersions(m: Material): Promise<void> {
    const versions = await localFiles.versions(m.id).catch(() => []);
    const when = (iso: string) =>
      new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    const handle = openModal({
      title: `Versionen – ${m.title}`,
      guardUnsaved: false,
      body: versions.length
        ? h(
            "div",
            { class: "material-list" },
            ...versions.map((v) =>
              h(
                "div",
                { class: "material-row" },
                h("span", { class: "m-icon", html: icon("clock", 20) }),
                h(
                  "div",
                  { class: "m-body" },
                  h("div", { class: "m-name" }, when(v.at)),
                  h("div", { class: "m-meta" }, [v.label, formatBytes(v.blob.size)].filter(Boolean).join(" · "))
                ),
                button({
                  icon: "eye",
                  variant: "ghost",
                  size: "sm",
                  title: "Ansehen",
                  onClick: () => window.open(URL.createObjectURL(v.blob), "_blank"),
                }),
                button({
                  label: "Wiederherstellen",
                  size: "sm",
                  onClick: async () => {
                    try {
                      // Der jetzige Stand wird dabei selbst zur Version – nichts geht verloren.
                      await localFiles.saveLocal(m.id, v.blob, "Vor dem Wiederherstellen", true);
                      schedulePush(500);
                      handle.close();
                      toast("Version wiederhergestellt.", "success");
                      refreshLocal();
                    } catch (err) {
                      toast(err instanceof Error ? err.message : pbErrorMessage(err), "error");
                    }
                  },
                })
              )
            )
          )
        : h(
            "p",
            { class: "muted" },
            "Noch keine älteren Versionen auf diesem Gerät. Sie entstehen beim Bearbeiten im Arbeits-PDF (eine je Sitzung) und beim Abgleich mit anderen Geräten."
          ),
    });
  }

  // ---- Mitschrift | Lernzettel ----
  function noteCard(kind: NoteKind, label: string, ic: IconName): HTMLElement {
    const doc = noteOf(kind);
    const content = (doc?.content ?? "").trim();
    // Zeilen bleiben Zeilen (CSS: white-space: pre-line) – nur die
    // Markdown-Zeichen fallen weg. Das „…" beim Überlaufen macht die
    // Zeilenbegrenzung in der CSS.
    const preview = content
      ? content
          .replace(/^[ \t]*:::.*$/gm, "")
          .replace(/[#>*`_~]/g, "")
          .replace(/\n{3,}/g, "\n\n")
          .trim()
          .slice(0, 600)
      : "Noch nichts geschrieben – tippen, um zu starten.";
    return h(
      "button",
      { class: "note-card", onclick: () => navigate(`/topic/${id}/edit/${kind}`) },
      h("div", { class: "nc-head" }, h("span", { class: "row", html: icon(ic, 18) }), label),
      h("div", { class: "nc-preview" }, preview),
      h("div", { class: "nc-foot" }, content ? `${wordCount(content)} Wörter` : "Leer")
    );
  }

  const notesRow = h(
    "div",
    { class: "notes-grid" },
    noteCard("mitschrift", "Mitschrift", "pencil"),
    noteCard("lernzettel", "Lernzettel", "note")
  );

  // ---- Üben ----
  // Die wichtigsten Methoden als Kacheln (Direktstart), Rest hinter „Alle".
  // "tiles" = Methodenkacheln, "button" = nur ein blauer Knopf unten.
  const UEBEN_DESIGN: "tiles" | "button" = "button";

  const topMethods: { route: string; title: string; ic: IconName; desc: string }[] = [
    { route: `/topic/${id}/pfad`,           title: "Lernpfad",     ic: "target",  desc: "Sequenzen, Lektionen, AFB 1–3" },
    { route: `/topic/${id}/karten`,         title: "Karteikarten", ic: "layers",  desc: "Leitner, Zuordnen, Prüfung" },
    { route: `/topic/${id}/vortrag/recall`, title: "Vortrag",      ic: "mic",     desc: "Frei sprechen, KI bewertet" },
    { route: `/topic/${id}/klausur`,        title: "Probeklausur", ic: "pencil",  desc: "Echte Quelle, Timer & Korrektur" },
  ];

  const uebenSection =
    UEBEN_DESIGN === "button"
      ? h("div", { class: "learn-cta" },
          h("button", { class: "learn-cta-btn", onclick: () => navigate(`/topic/${id}/learn`) },
            h("span", { class: "row", html: icon("target", 22) }), "Jetzt lernen"))
      : h("div", { class: "card section" },
          h("div", { class: "section-title" },
            h("span", { class: "row", html: icon("target", 18) }), "Üben",
            h("span", { class: "spacer" }),
            h("button", { class: "subject-expand", onclick: () => navigate(`/topic/${id}/learn`) },
              "Alle Methoden", h("span", { class: "row", html: icon("chevron-right", 14) }))),
          h("div", { class: "uebung-hub" },
            ...topMethods.map((m) =>
              h("button", { class: "uebung-tile", onclick: () => navigate(m.route) },
                h("span", { class: "row ut-icon", html: icon(m.ic, 22) }),
                h("span", { class: "ut-title" }, m.title),
                h("span", { class: "ut-desc" }, m.desc)))),
          h("div", { class: "uebung-cta" },
            button({ label: "Üben starten", icon: "chevron-right", variant: "primary", block: true,
              onClick: () => navigate(`/topic/${id}/learn`) })));

  // Die Hausaufgaben-Banner über der Themenseite sind vorerst still gelegt –
  // sie stecken jetzt im Kreis neben dem Zahnrad (siehe homeworkBadge).
  return h(
    "div",
    null,
    header,
    h("div", { class: "sections" }, materialsSection, notesRow, uebenSection)
  );
}

function backButton(): HTMLElement {
  return button({
    icon: "chevron-left",
    label: "Zurück",
    variant: "ghost",
    size: "sm",
    onClick: () => navigate("/"),
  });
}
